import { useCallback, useEffect, useState } from 'react';
import {
  signIn as cognitoSignIn,
  completeNewPassword as cognitoCompleteNewPassword,
  forgotPassword as cognitoForgotPassword,
  confirmForgotPassword as cognitoConfirmForgotPassword,
  signOut as cognitoSignOut,
  hasValidSession,
  getIdToken,
  currentUserEmail,
  type SignInResult,
  type NewPasswordChallenge,
} from '../../components/shared/auth/cognito';
import { bypassAdminAuth } from '../../config';

type AuthStatus = 'checking' | 'signed-out' | 'signed-in';

const BYPASS = bypassAdminAuth();

/** Manages Cognito sign-in state for the admin area. */
export function useAuth() {
  const [status, setStatus] = useState<AuthStatus>(BYPASS ? 'signed-in' : 'checking');
  const [email, setEmail] = useState<string | undefined>(BYPASS ? 'local@dev' : undefined);

  useEffect(() => {
    if (BYPASS) return;
    hasValidSession().then((ok) => {
      setStatus(ok ? 'signed-in' : 'signed-out');
      if (ok) currentUserEmail().then(setEmail);
    });
  }, []);

  // Flip local state to signed-in once a session exists (normal sign-in or after
  // completing a first-login challenge). Shared by signIn / completeNewPassword.
  const markSignedIn = useCallback(() => {
    setStatus('signed-in');
    currentUserEmail().then(setEmail);
  }, []);

  const signIn = useCallback(
    async (user: string, password: string): Promise<SignInResult> => {
      const result = await cognitoSignIn(user, password);
      if (result.kind === 'ok') markSignedIn();
      return result; // 'newPasswordRequired' handled by the caller (login form)
    },
    [markSignedIn],
  );

  const completeNewPassword = useCallback(
    async (challenge: NewPasswordChallenge, newPassword: string) => {
      await cognitoCompleteNewPassword(challenge, newPassword);
      markSignedIn();
    },
    [markSignedIn],
  );

  const signOut = useCallback(() => {
    if (BYPASS) return; // nothing to sign out of locally
    cognitoSignOut();
    setStatus('signed-out');
    setEmail(undefined);
  }, []);

  // In bypass mode there's no Cognito session, so hand back a placeholder token
  // (the mock API ignores it; a real backend would reject it — bypass is local).
  const token = BYPASS ? () => Promise.resolve('local-dev-token') : getIdToken;

  // Recovery flows don't touch signed-in state; expose them through the same
  // seam so the forms depend only on useAuth.
  const forgotPassword = useCallback((user: string) => cognitoForgotPassword(user), []);
  const confirmForgotPassword = useCallback(
    (user: string, code: string, newPassword: string) =>
      cognitoConfirmForgotPassword(user, code, newPassword),
    [],
  );

  return {
    status,
    email,
    signIn,
    completeNewPassword,
    forgotPassword,
    confirmForgotPassword,
    signOut,
    getIdToken: token,
  };
}
